// FILE: src/utils/passwordPolicy.js
// Mirrors the server policy (zun-lms-backend src/utils/password.js; D10):
// at least 8 characters with upper- and lower-case letters, a number and a
// symbol. The server has the final say; this only helps the student.
export const PASSWORD_RULES = [
  { id: "length", label: "At least 8 characters", test: (p) => p.length >= 8 },
  { id: "upper", label: "An uppercase letter (A–Z)", test: (p) => /[A-Z]/.test(p) },
  { id: "lower", label: "A lowercase letter (a–z)", test: (p) => /[a-z]/.test(p) },
  { id: "digit", label: "A number (0–9)", test: (p) => /\d/.test(p) },
  { id: "symbol", label: "A symbol or punctuation mark (e.g. ! ? # @)", test: (p) => /[^A-Za-z0-9\s]/.test(p) }
];

export function passwordFailures(password = "") {
  return PASSWORD_RULES.filter((r) => !r.test(password)).map((r) => r.id);
}

// Strength meter (guidance only; the rules above are the gate). Four
// levels from length, character variety and easy-to-guess patterns.
export const STRENGTH_LABELS = ["", "Too weak", "Weak", "Good", "Strong"];

const COMMON_WORDS = /pass(w|vv)?(o|0)rd|qwerty|asdf|letmein|welcome|iloveyou|admin|monkey|dragon|sunshine|zun ?drivers?/i;
const REPEATS = /(.)\1\1/; // "aaa", "111"

function hasRun(password, size = 4) {
  const s = password.toLowerCase();
  for (let i = 0; i + size <= s.length; i++) {
    let up = true;
    let down = true;
    for (let j = 1; j < size; j++) {
      const d = s.charCodeAt(i + j) - s.charCodeAt(i + j - 1);
      if (d !== 1) up = false;
      if (d !== -1) down = false;
    }
    if ((up || down) && /^[a-z0-9]+$/.test(s.slice(i, i + size))) return true;
  }
  return false;
}

/**
 * { level: 0–4, label, tip } — 0 means nothing typed yet. A password that
 * fails a rule is always "Too weak"; past the rules, length and a mix of
 * numbers and symbols raise it, common words, repeats and runs lower it.
 */
export function passwordStrength(password = "") {
  if (!password) return { level: 0, label: STRENGTH_LABELS[0], tip: "" };
  if (passwordFailures(password).length) {
    return { level: 1, label: STRENGTH_LABELS[1], tip: "Meet every rule in the list below." };
  }
  let points = 0;
  if (password.length >= 10) points++;
  if (password.length >= 14) points++;
  if ((password.match(/[^A-Za-z]/g) || []).length >= 3) points++;
  if (COMMON_WORDS.test(password)) points -= 2;
  if (REPEATS.test(password) || hasRun(password)) points--;
  const level = points <= 0 ? 2 : points <= 2 ? 3 : 4;
  const tip = {
    2: "Easy to guess. Make it longer and avoid common words, repeats like \"aaa\" and runs like \"1234\".",
    3: "To make it strong, add length: a few unrelated words joined by numbers or symbols work well.",
    4: "Hard to guess. Keep it to yourself."
  }[level];
  return { level, label: STRENGTH_LABELS[level], tip };
}
